/**
 * 見積書の比較（036、docs/27 §3。#188 で作り直し＝docs/27 §7、Figma 173:352。前の題は「回の比較」）。/estimates/:id/compare?from=1&to=3
 *   ・頭は見積書と同じ2行の帯（.eshead）：1行目＝‹ 見積書・題・チップ（件名（番号））・「？」／右に 合計（税込）前 → 今・増減・税抜。
 *     2行目＝[1回目（10/11）⌄] → [2回目（下書き）⌄]・件数／右に 変更行のみ表示（既定オン）・社内用（見積書と同じ端末の設定 gb.est.internal）
 *     （前の「前の回と」「1回目と最新」は無くした：開いた直後の組を選び直すだけ。2つのセレクトで足りる）
 *   ・下は1つの紙：内訳のある見積は見積書と同じ内訳タブ（表紙＋内訳。下段は 前 → 今 の小計）。表紙＝内訳ごとの増減の表、内訳＝その紙の明細。
 *     紙の無い見積はタブ無しで明細だけ。いちばん下に 小計 → 消費税 → 合計（税込）を 前 → 今 で
 *   ・明細の変えた欄は2段（上＝前 13px 薄墨・下＝今）、変わった行は淡い地。印は文字（変更／追加／削除／計算。色だけで伝えない）
 *   ・社内用：仕入れ・利益の列。利益は仕入れ未入力があれば出さない（ルール7）。原価割れは文字でも
 *   ・差分も金額も DB が出したもの（fn_revision_diff・fn_revision_sheet_diff・版の合計）。ここでは計算しない（増減と利益の引き算だけ）
 *   ・スモール・ビジネスのプランだけ（無いプランは 403 → 案内）
 *   ・{order}の比較（#233 2b、/orders/:id/compare）も同じ画面：doc="orders"。頭の欄の上に条件の変更（API が heads に入れる）・社内用の列は無い（この紙の金額が仕入れ）・プランを問わない
 */
import { COMPARE_MSG, ESTIMATE_PC_MSG, ORDER_REVISE_MSG, RATE_BASE_LABEL, SHEET_USE_LABEL, type CompareLine, type RevisionCompare, type RevisionRounds } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { DocField, DocHead } from "../components/DocHead.jsx";
import { PcLink } from "../components/PcLink.jsx";
import { ErrorText, Help, Switch } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { fq, mdSlash, yen } from "../lib/num.js";
import { useShowInternal } from "./estimates/EstimateTable.jsx";

const M = COMPARE_MSG;
type Change = CompareLine["change"];
const CHIP_CLASS: Record<Change, string> = { added: "ai", changed: "ai", removed: "shu", calc: "calc", cost: "calc", same: "" };
function ChangeChip({ change }: { change: Change }) {
  if (change === "same") return null;
  return <span className={"chip " + CHIP_CLASS[change]}>{M.change[change]}</span>;
}

/** 増減（＋は墨、−は朱。記号を文字で添える） */
function Delta({ d, unit = "" }: { d: number; unit?: string }) {
  if (d === 0) return <span className="muted">±0</span>;
  return (
    <span className={"num" + (d < 0 ? " shu" : "")}>
      {d > 0 ? "＋" : "−"}
      {yen(Math.abs(d))}
      {unit}
    </span>
  );
}

/** 2段：変わっていれば 上段＝前（13px・薄墨）／下段＝今。同じなら1段 */
function Two({ o, n, show }: { o: string; n: string; show: boolean }) {
  if (!show || o === n) return <span>{n || o}</span>;
  return (
    <>
      <span className="cmpold">{o || "（空）"}</span>
      <span className="cmpnew">{n || "（空）"}</span>
    </>
  );
}

/** 前 → 今（片方が無いときは —） */
const arrow = (o: number | null, n: number | null) => `${o == null ? "—" : yen(o)} → ${n == null ? "—" : yen(n)}`;

type Sheet = RevisionCompare["sheets"][number];
const COVER = "cover";

export function OrderComparePage() {
  return <EstimateComparePage doc="orders" />;
}

export function EstimateComparePage({ doc = "estimates" }: { doc?: "estimates" | "orders" } = {}) {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const L = useLabels();
  const isOrder = doc === "orders";
  const docName = isOrder ? L.order : "見積書";
  const title = isOrder ? t(ORDER_REVISE_MSG.compareTitle) : M.title;
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ その見積書（{order}）
  const back = useBackNav({ to: `/${doc}/${id}`, label: docName });
  usePageName(title);
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [internalSel, setInternal] = useShowInternal();
  // {order}には社内用の列が無い（この紙の金額が仕入れ）
  const internal = !isOrder && internalSel;
  const [tab, setTab] = useState<string>(COVER);
  const rounds = useQuery({ queryKey: [isOrder ? "order-rounds" : "estimate-rounds", id], queryFn: () => api<RevisionRounds>("GET", `/${doc}/${id}/revisions`), retry: false });
  const list = rounds.data?.rounds ?? [];
  const last = list.length ? list[list.length - 1]!.no : 0;
  const to = Number(sp.get("to")) || last;
  const from = Number(sp.get("from")) || Math.max(1, to - 1);
  const ok = list.length >= 2 && from !== to && list.some((r) => r.no === from) && list.some((r) => r.no === to);
  const cmp = useQuery({
    queryKey: [isOrder ? "order-compare" : "estimate-compare", id, from, to],
    queryFn: () => api<RevisionCompare>("GET", `/${doc}/${id}/compare?from=${from}&to=${to}`),
    enabled: ok,
    retry: false,
  });
  const setPair = (f: number, t2: number) => setSp({ from: String(f), to: String(t2) }, { replace: true });

  if (rounds.isPending) return <p className="hint">読み込んでいます…</p>;
  if (rounds.isError) {
    const plan = rounds.error instanceof ApiError && rounds.error.problem.code === "PLAN_FEATURE";
    return (
      <div className="soonbox" data-testid="compare-blocked">
        <ErrorText>{messageOf(rounds.error)}</ErrorText>
        <p>
          {plan ? <PcLink className="btn text" to="/account/plan">料金プラン表示</PcLink> : null}　<Link className="btn text" to={`/${doc}/${id}`}>{isOrder ? `${docName}へ` : "見積へ"}</Link>
        </p>
      </div>
    );
  }
  if (list.length < 2) {
    return (
      <div className="soonbox">
        <p>{isOrder ? `この${docName}は、まだ1回しか発行していません。比較できるのは、訂正版を作成したあとです。` : "この見積は、まだ1回しか発行していません。比較できるのは、訂正版を発行したあとです。"}</p>
        <p><Link className="btn text" to={`/${doc}/${id}`}>{isOrder ? `${docName}へ` : "見積へ"}</Link></p>
      </div>
    );
  }

  const c = cmp.data;
  const label = (no: number) => {
    const r = list.find((x) => x.no === no);
    return r ? `${no}回目（${r.state === "draft" ? "下書き" : mdSlash(r.issueDate)}）` : `${no}回目`;
  };
  /** 出す行：変更行のみ表示なら「同じ」を出さない。仕入れだけの変更は社内用を出しているときだけ */
  const shown = (l: CompareLine) => !onlyChanged || (l.change !== "same" && (l.change !== "cost" || internal));
  const cover = c?.sheets.find((x) => x.type === "cover");
  const breakdowns = c?.sheets.filter((x) => x.type !== "cover") ?? [];
  const tabbed = !!c && c.sheets.length > 0;
  const cur = tabbed && tab !== COVER && breakdowns.some((x) => x.key === tab) ? tab : COVER;
  /** その紙の行（表紙は 表紙の行と、紙に付いていない行） */
  const linesOf = (key: string) => (c?.lines ?? []).filter((l) => (key === COVER ? l.sheetKey == null || l.sheetKey === cover?.key || !breakdowns.some((b) => b.key === l.sheetKey) : l.sheetKey === key));
  const profit = c && c.cost.old != null && c.cost.new != null ? { old: c.from.subtotal - c.cost.old, new: c.to.subtotal - c.cost.new } : null;
  const showReimb = !!c && (c.from.reimbursementTotal !== 0 || c.to.reimbursementTotal !== 0);

  return (
    <>
      {/* #245（docs/94 B-4 案B）：頭は DocHead の3段。2段目＝見積書の比較・件名（番号）｜合計 前 → 後。3段目＝くらべる回・件数｜変更行のみ・社内用 */}
      <DocHead
        back={back}
        kind="estimates"
        kindLabel={title}
        title={
          c ? (
            <span className="dh-ttext" data-testid="compare-name" title={c.subject}>
              {c.subject || "（件名なし）"}
              {c.documentNo ? `（${c.documentNo}）` : ""}
            </span>
          ) : null
        }
        help={<Help testId="help-compare" label={`${title}の説明`} text={M.help} />}
        totalNode={
          c ? (
            <div data-testid="compare-total">
              <div className="n">
                <span className="tl">{M.total}</span>
                <span className="num cmpbig">{yen(c.from.total)} → {yen(c.to.total)}</span>
                <span className="yen">円</span>
              </div>
              <div className="sub num">
                <b><Delta d={c.to.total - c.from.total} unit="円" /></b>　{M.ex} {yen(c.from.subtotal)} → {yen(c.to.subtotal)}円
              </div>
            </div>
          ) : null
        }
        meta={
          <>
            <DocField label="回">
              <span className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
                <select className="cmpsel" aria-label={M.from} value={from} onChange={(e) => setPair(Number(e.target.value), to)} data-testid="compare-from">
                  {list.map((r) => <option key={r.no} value={r.no} disabled={r.no === to}>{label(r.no)}</option>)}
                </select>
                <span aria-hidden="true">→</span>
                <select className="cmpsel" aria-label={M.to} value={to} onChange={(e) => setPair(from, Number(e.target.value))} data-testid="compare-to">
                  {list.map((r) => <option key={r.no} value={r.no} disabled={r.no === from}>{label(r.no)}</option>)}
                </select>
              </span>
            </DocField>
            {c ? (
              <span className="cmpcounts" data-testid="compare-counts">
                変更 {c.counts.changed}・追加 {c.counts.added}・削除 {c.counts.removed}・計算 {c.counts.calc}・同じ {c.counts.same}
              </span>
            ) : null}
          </>
        }
        tools={
          <span className="estools">
            <span className="intsw">
              <span className="muted">{M.onlyChanged}</span>
              <Switch checked={onlyChanged} onChange={setOnlyChanged} label={M.onlyChanged} testId="compare-only" bare />
            </span>
            {isOrder ? null : (
              <span className="intsw">
                <span className="muted">{M.internal}</span>
                <Switch checked={internal} onChange={setInternal} label={M.internal} testId="compare-internal" bare />
              </span>
            )}
          </span>
        }
        testId="compare-band"
      >
        {c?.revisionNote ? <p className="cmpnote">{M.note.replace("{n}", String(c.to.no)).replace("{note}", c.revisionNote)}</p> : null}
      </DocHead>

      {cmp.isError ? <ErrorText>{messageOf(cmp.error)}</ErrorText> : null}
      {!ok ? <p className="hint">ちがう回を2つ選んでください。</p> : null}
      {ok && cmp.isPending ? <p className="hint">比較中…</p> : null}

      {c ? (
        <div className={"paper esheet cmpsheet" + (internal ? " is-internal" : "")} data-testid="compare-sheet">
          {tabbed ? (
            <div className="etools">
              <div className="etools-l">
                <div className="etabs" role="tablist" aria-label={M.sheets} data-testid="compare-tabs">
                  <button type="button" role="tab" aria-selected={cur === COVER} className={"etab cover" + (cur === COVER ? " is-on" : "")} onClick={() => setTab(COVER)} data-testid="compare-tab-cover">
                    <b><span className="covt">{M.cover}</span></b>
                    <span className="num tsub">{breakdowns.filter((x) => x.newSubtotal != null && x.inUse.new !== false).length}内訳</span>
                  </button>
                  {breakdowns.map((x) => (
                    <button key={x.key} type="button" role="tab" aria-selected={cur === x.key} className={"etab" + (cur === x.key ? " is-on" : "") + (x.inUse.new === false ? " is-unused" : "")} onClick={() => setTab(x.key)} data-testid="compare-tab">
                      <b>
                        {x.name || "（名前なし）"}
                        <SheetChip x={x} tab />
                      </b>
                      <span className="num tsub">{arrow(usedSubtotal(x, "old"), usedSubtotal(x, "new"))}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {tabbed && cur === COVER ? (
            <table className="cmptable cmpsheets" data-testid="compare-sheets">
              <colgroup>
                <col style={{ width: 80 }} />
                <col />
                <col style={{ width: 160 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 100 }} />
              </colgroup>
              <thead>
                <tr><th>印</th><th>{M.sheetCol}</th><th className="r">前</th><th className="r">今</th><th className="r">増減</th><th className="r">{M.changedLines}</th></tr>
              </thead>
              <tbody>
                {breakdowns.map((x) => (
                  <tr key={x.key} className={sheetChanged(x) ? "is-changed" : ""}>
                    <td><SheetChip x={x} /></td>
                    <td>
                      <button type="button" className="cmpgo" onClick={() => setTab(x.key)}>{x.name || "（名前なし）"}</button>
                    </td>
                    {/* #199：不使用の内訳は合計に入らない＝金額の代わりに「不使用」・増減は 0 として数える */}
                    <td className="r num">{x.inUse.old === false ? SHEET_USE_LABEL.unused : x.oldSubtotal == null ? "—" : yen(x.oldSubtotal)}</td>
                    <td className="r num">{x.inUse.new === false ? SHEET_USE_LABEL.unused : x.newSubtotal == null ? "—" : yen(x.newSubtotal)}</td>
                    <td className="r"><Delta d={(usedSubtotal(x, "new") ?? 0) - (usedSubtotal(x, "old") ?? 0)} /></td>
                    <td className="r num">{x.changedCount || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          <LineTable
            heads={!tabbed || cur === COVER ? c.heads : []}
            lines={(tabbed ? linesOf(cur) : c.lines).filter(shown)}
            internal={internal}
            isOrder={isOrder}
            // 表紙の行も頭の欄の変更も無い表紙は、明細の表を出さない（内訳ごとの増減の表だけ）
            hideEmpty={tabbed && cur === COVER}
          />

          <div className="cmpfoot" data-testid="compare-foot">
            <div className="cmpfrow">
              <span className="k">{M.subtotal}</span>
              <span className="num v">{arrow(c.from.subtotal, c.to.subtotal)}</span>
            </div>
            <div className="cmpfrow">
              <span className="k">{M.tax}</span>
              <span className="num v">{arrow(c.from.taxTotal, c.to.taxTotal)}</span>
            </div>
            {showReimb ? (
              <div className="cmpfrow">
                <span className="k">{M.reimbursement}</span>
                <span className="num v">{arrow(c.from.reimbursementTotal, c.to.reimbursementTotal)}</span>
              </div>
            ) : null}
            <div className="cmpfrow is-total" data-testid="compare-foot-total">
              <span className="k">{M.total}</span>
              <span className="num v">{arrow(c.from.total, c.to.total)}</span>
            </div>
            {internal ? (
              <div className="cmpin" data-testid="compare-inhouse">
                <b>社内用（{L.customer}には出ません）</b>
                {profit && c.cost.old != null && c.cost.new != null ? (
                  <>
                    仕入れ <span className="num">{yen(c.cost.old)} → {yen(c.cost.new)}</span>　利益 <span className="num">{yen(profit.old)} → {yen(profit.new)}</span>（<Delta d={profit.new - profit.old} />）
                    {profit.new < 0 ? <span className="chip shu" style={{ marginLeft: 8 }}>{M.under}</span> : null}
                  </>
                ) : (
                  <span>{M.costMissing}</span>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** 合計に入る内訳の小計（#199：不使用の回は 0。その回に無ければ null） */
const usedSubtotal = (x: Sheet, side: "old" | "new"): number | null => {
  const v = side === "old" ? x.oldSubtotal : x.newSubtotal;
  return v == null ? null : x.inUse[side] === false ? 0 : v;
};
/** 使う／不使用 が変わった内訳（#199） */
const useFlipped = (x: Sheet) => x.inUse.old != null && x.inUse.new != null && x.inUse.old !== x.inUse.new;
const sheetChanged = (x: Sheet) => useFlipped(x) || (x.inUse.old !== false && x.inUse.new !== false && (x.oldSubtotal == null || x.newSubtotal == null || x.changedCount > 0 || x.oldSubtotal !== x.newSubtotal));
/** 内訳の印：追加（前に無い）／削除（今に無い）／変更（変わった行がある。タブでは件数も）／計算（行は同じで小計だけ変わった）。#199：不使用 → 使用・不使用 */
function SheetChip({ x, tab = false }: { x: Sheet; tab?: boolean }) {
  const cls = tab ? " tabmk" : "";
  const use = (v: boolean | null) => (v === false ? SHEET_USE_LABEL.unused : SHEET_USE_LABEL.used);
  if (useFlipped(x)) return <span className={"chip ai" + cls} data-testid="compare-sheet-use">{use(x.inUse.old)} → {use(x.inUse.new)}</span>;
  if (x.inUse.new === false || (x.newSubtotal == null && x.inUse.old === false)) return <span className={"sheet-unused" + cls}>{SHEET_USE_LABEL.unused}</span>;
  if (x.oldSubtotal == null) return <span className={"chip ai" + cls}>{M.change.added}</span>;
  if (x.newSubtotal == null) return <span className={"chip shu" + cls}>{M.change.removed}</span>;
  if (x.changedCount > 0) return <span className={"chip ai" + cls}>{M.change.changed}{tab ? ` ${x.changedCount}` : ""}</span>;
  if (x.oldSubtotal !== x.newSubtotal) return <span className={"chip calc" + cls}>{M.change.calc}</span>;
  return null;
}

/** 明細の表（列の幅は見積書の表と同じ作法。社内用は 仕入れ・利益） */
function LineTable({ heads, lines, internal, hideEmpty, isOrder = false }: { heads: RevisionCompare["heads"]; lines: CompareLine[]; internal: boolean; hideEmpty: boolean; isOrder?: boolean }) {
  if (hideEmpty && !heads.length && !lines.length) return null;
  const cols = internal ? 9 : 7;
  return (
    <table className="cmptable cmplines" data-testid="compare-lines">
      <colgroup>
        <col style={{ width: 80 }} />
        <col />
        <col style={{ width: 80 }} />
        <col style={{ width: 60 }} />
        <col style={{ width: 120 }} />
        <col style={{ width: 120 }} />
        <col style={{ width: 120 }} />
        {internal ? (
          <>
            <col style={{ width: 140 }} />
            <col style={{ width: 120 }} />
          </>
        ) : null}
      </colgroup>
      <thead>
        <tr>
          <th>印</th><th>{t("{lineName}・{lineSpec}")}</th><th className="r">数量</th><th>単位</th><th className="r">単価</th><th className="r">金額</th><th className="r">増減</th>
          {internal ? (
            <>
              <th className="r inh">仕入れ</th><th className="r inh">利益</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {heads.map((h, i) => (
          <tr key={"h" + i} className="cmp-changed" data-testid="compare-head">
            <td><ChangeChip change="changed" /></td>
            <td colSpan={cols - 1}>
              {h.label}
              {/* {order}の条件で前回の値を持たない行（行の内訳の「変更（行の名前）」）は前回の欄を出さない＝発行前の確認と同じ */}
              {h.old || !isOrder ? <span className="cmpold" style={{ whiteSpace: "pre-wrap" }}>{h.old || "（空）"}</span> : null}
              <span className="cmpnew" style={{ whiteSpace: "pre-wrap" }}>{h.new || "（空）"}</span>
            </td>
          </tr>
        ))}
        {lines.map((l, i) => (
          <LineRow key={i} l={l} internal={internal} />
        ))}
        {!heads.length && !lines.length ? <tr><td colSpan={cols} className="cmpempty">{M.noChange}</td></tr> : null}
      </tbody>
    </table>
  );
}

function LineRow({ l, internal }: { l: CompareLine; internal: boolean }) {
  const ch = l.change === "changed";
  const valued = l.role === "detail" || l.role === "excluded";
  const qty = (v: number | null) => (v == null ? "" : fq(v));
  const money = (v: number | null) => (v == null ? "" : yen(v));
  const rate = (v: number | null) => (v == null ? "" : `${Math.round(v * 10000) / 100}%`);
  const rb = (v: CompareLine["rateBase"]["new"]) => (v == null ? "" : RATE_BASE_LABEL[v]);
  const costText = (v: number | null) => (v == null ? "未入力" : yen(v));
  const amtChanged = l.amount.old != null && l.amount.new != null && l.amount.old !== l.amount.new;
  // 利益＝金額 − 仕入れ（明細の行だけ。仕入れ未入力なら出さない＝ルール7）
  const pr = (a: number | null, c: number | null) => (a == null || c == null ? null : a - c);
  const prOld = pr(l.amount.old, l.cost.old);
  const prNew = pr(l.amount.new, l.cost.new);
  const prText = (v: number | null) => (v == null ? "—" : yen(v));
  let cost: ReactNode = null;
  let profit: ReactNode = null;
  if (l.role === "detail") {
    if (l.change === "removed") {
      cost = costText(l.cost.old);
      profit = prText(prOld);
    } else if (l.change === "added") {
      cost = costText(l.cost.new);
      profit = prText(prNew);
    } else {
      cost = <Two o={costText(l.cost.old)} n={costText(l.cost.new)} show />;
      profit = <Two o={prText(prOld)} n={prText(prNew)} show />;
    }
  }
  return (
    <tr className={"cmp-" + l.change} data-testid="compare-line">
      <td><ChangeChip change={l.change} /></td>
      <td>
        <span className="cmpname"><Two o={l.name.old} n={l.name.new} show={ch} /></span>
        {l.spec.old || l.spec.new ? <span className="muted cmpspec"><Two o={l.spec.old} n={l.spec.new} show={ch} /></span> : null}
        {l.role === "overhead_rate" ? <span className="muted cmpspec"><Two o={rate(l.rate.old)} n={rate(l.rate.new)} show={ch} /></span> : null}
        {l.role === "overhead_rate" && ch && l.rateBase.old !== l.rateBase.new ? <span className="muted cmpspec">{ESTIMATE_PC_MSG.rateTarget} <Two o={rb(l.rateBase.old)} n={rb(l.rateBase.new)} show /></span> : null}
        {ch && l.note.old !== l.note.new ? <span className="muted cmpspec">{t("{lineNote}")} <Two o={l.note.old} n={l.note.new} show /></span> : null}
        {l.movedFrom ? <span className="muted cmpspec">「{l.movedFrom}」から移した</span> : null}
      </td>
      <td className="r num">{valued ? <Two o={qty(l.quantity.old)} n={qty(l.quantity.new)} show={ch} /> : null}</td>
      <td>{valued ? <Two o={l.unit.old} n={l.unit.new} show={ch} /> : null}</td>
      <td className="r num">{valued ? <Two o={money(l.unitPrice.old)} n={money(l.unitPrice.new)} show={ch} /> : null}</td>
      <td className="r num">
        {l.amountLabel.new ?? l.amountLabel.old ? <span>{l.amountLabel.new ?? l.amountLabel.old}</span> : <Two o={money(l.amount.old)} n={money(l.amount.new)} show={amtChanged} />}
      </td>
      <td className="r">{l.change === "same" || l.change === "cost" ? null : <Delta d={(l.amount.new ?? 0) - (l.amount.old ?? 0)} />}</td>
      {internal ? (
        <>
          <td className="r num inh">{cost}</td>
          <td className="r num inh">
            {profit}
            {prNew != null && prNew < 0 && l.change !== "removed" ? <span className="chip shu cmpunder">{M.under}</span> : null}
          </td>
        </>
      ) : null}
    </tr>
  );
}
